/**
 * Neutralizes executable <script> tags inside a raw CMS HTML string.
 *
 * Browsers never execute <script> elements that arrive inside a string
 * assigned via innerHTML (which is exactly what dangerouslySetInnerHTML
 * does). On a hard navigation the browser's native HTML parser executes
 * them fine, but on a client-side (soft) navigation React only patches
 * .innerHTML, leaving them inert forever.
 *
 * To make script execution deterministic in every scenario, executable
 * <script> tags are marked inert here (type="text/plain" + data-cms-script)
 * so the browser's native parser never auto-executes them either — a
 * companion client hook (useReplayCmsScripts) always replays them via
 * document.createElement("script"), exactly once, regardless of whether
 * this is a hard load or a soft navigation.
 *
 * The invariant this helper must hold is *parity with the browser*: it
 * neutralizes exactly what the native parser would have executed, and
 * leaves everything else byte-for-byte untouched. Both directions of a
 * mismatch are silent bugs — classify an executable script as data and it
 * stays inert on soft navigation (the very bug this exists to fix);
 * classify a data block as executable and the replay runs it as JS
 * (a JSON-LD block becomes a SyntaxError and disappears from the DOM).
 *
 * That parity is why the type check below mirrors the HTML spec literally:
 * the trimmed `type` value must be empty, "module", or an exact match for a
 * JavaScript MIME type essence. Parameters are deliberately NOT stripped —
 * `type="text/javascript;charset=UTF-8"` is not an essence match, so modern
 * browsers do not execute it, and neither do we.
 *
 * Known limitation: the scan skips the body of <script> elements, but not
 * the body of escapable raw-text elements such as <textarea>/<title>. CMS
 * pages that *display* script markup as example text (glossario,
 * guia-de-tamanhos) get the extra attributes in the visible text. Cosmetic
 * only — nothing is executed either way.
 */
const SCRIPT_OPEN_TAG = /<script(?=[\s/>])/gi;

// Anchored on a whitespace boundary (not \b) so `data-type=`, `data-mime-type=`
// and friends are not mistaken for the script's own `type` attribute. The
// third alternative accepts unquoted values, so they are recognized *and*
// stripped instead of producing a duplicate `type` attribute.
const TYPE_ATTR = /(?:^|\s)type\s*=\s*(?:(["'])(.*?)\1|([^\s"'>]+))/i;

// JavaScript MIME type essences, per the HTML/MIME Sniffing specs.
const JS_MIME_ESSENCES = new Set([
  "application/ecmascript",
  "application/javascript",
  "application/x-ecmascript",
  "application/x-javascript",
  "text/ecmascript",
  "text/javascript",
  "text/javascript1.0",
  "text/javascript1.1",
  "text/javascript1.2",
  "text/javascript1.3",
  "text/javascript1.4",
  "text/javascript1.5",
  "text/jscript",
  "text/livescript",
  "text/x-ecmascript",
  "text/x-javascript",
]);

type ScriptKind = "classic" | "module" | "data";

function classify(attrs: string): ScriptKind {
  const match = TYPE_ATTR.exec(attrs);
  if (!match) return "classic";

  const type = (match[2] ?? match[3] ?? "").trim().toLowerCase();
  if (type === "") return "classic";
  if (type === "module") return "module";
  return JS_MIME_ESSENCES.has(type) ? "classic" : "data";
}

/**
 * Index of the ">" that closes an open tag, ignoring ">" inside quoted
 * attribute values (e.g. `<script data-cond="a>b">`). -1 when unterminated.
 */
function findOpenTagEnd(html: string, from: number): number {
  let quote: string | null = null;

  for (let i = from; i < html.length; i++) {
    const char = html[i];

    if (quote) {
      if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === ">") return i;
  }

  return -1;
}

function rewriteOpenTag(attrs: string): string {
  const kind = classify(attrs);
  if (kind === "data") return `<script${attrs}>`;

  // The original type is dropped from the copied attributes and its only
  // meaningful distinction (module vs classic) is carried in data-cms-type,
  // which useReplayCmsScripts restores on the live element. Without it a
  // module would be replayed as a classic script and throw on import/export.
  const cmsType = kind === "module" ? ` data-cms-type="module"` : "";
  const attrsWithoutType = attrs.replace(TYPE_ATTR, "");

  return `<script data-cms-script="true" type="text/plain"${cmsType}${attrsWithoutType}>`;
}

export function neutralizeScripts(html: string): string {
  if (!html) return html;

  // Single pass with an explicit cursor: after each open tag the scan jumps
  // straight to the matching </script>, so `<script` occurrences *inside* a
  // script body (document.write('<script src=…>'), innerHTML concatenation,
  // …) are copied verbatim instead of rewritten. A global .replace() has no
  // notion of that boundary and corrupts those snippets — with double quotes
  // the injected attributes even terminate the JS string literal early.
  const lower = html.toLowerCase();
  let out = "";
  let cursor = 0;
  let match: RegExpExecArray | null;

  SCRIPT_OPEN_TAG.lastIndex = 0;

  while ((match = SCRIPT_OPEN_TAG.exec(html)) !== null) {
    const tagStart = match.index;
    const attrsStart = tagStart + match[0].length;
    const tagEnd = findOpenTagEnd(html, attrsStart);
    if (tagEnd === -1) break;

    const bodyStart = tagEnd + 1;
    const closeIndex = lower.indexOf("</script", bodyStart);
    const bodyEnd = closeIndex === -1 ? html.length : closeIndex;

    out += html.slice(cursor, tagStart);
    out += rewriteOpenTag(html.slice(attrsStart, tagEnd));
    out += html.slice(bodyStart, bodyEnd);

    cursor = bodyEnd;
    SCRIPT_OPEN_TAG.lastIndex = bodyEnd;
  }

  return out + html.slice(cursor);
}
